import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { v4 } from "uuid";
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, Menu, MenuItem, Typography } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { CSS } from '@dnd-kit/utilities';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useDndContext, useSensor, useSensors } from "@dnd-kit/core";
import { rectSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import FormField from "../common/FormField.js";
import FormInput from "./FormInput.js";
import { deleteField,sectionAutoSave,setFormData } from "../../slices/FbSections.js";
import { CloneIcon, ConfirmationInfoIcon, SettingIcon } from "../../../../assets/common-svg/CommonSvg.js";
import { notify } from "../../../../bmr/utils/methods/notification.js";
import DeleteModal from "../../../../bmr/components/common/BaseModal/DeleteModal/index.js";
import { incrementVersion } from "../../../../bmr/slices/formTemplate.js";

const FormSection = ({
  index,
  section,
  selectedSection,
  selectedField,
  setSelectedSection,
  setSelectedField,
  addNewSection,
  removeSection,
  noOfSections,
  isSectionAdd,
  setIsFieldAdd,
  isFieldAdd
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [secOpenVersionIncreaseModal, setSecOpenVersionIncreaseModal] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();
  const templateDetails = useSelector((state) => state?.bmrFbl?.fbTemplate?.items || []);
  const currentFormTemplate = templateDetails.find(template => template?.id === +id);
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
  id: section.section_uuid,
});

  const { active } = useDndContext();
  // const isDragging = active?.id === section.id;
  const dispatch = useDispatch()
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // border: isDragging ? "2px dashed blue" : "1px solid #ccc",
  };  

  const { formState: { errors }, control, getValues } = useFormContext();
  const { fields, append, remove, move :fieldMove } = useFieldArray({
    control,
    name: `sections.${index}.fields`
  });
  const sectionSchema = useWatch({control, name: "sections" });
  const sectionName = sectionSchema?.[index]?.name ?? section?.name ?? "";

  const open = Boolean(anchorEl);
  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };  

  const handleRemoveField =async ({ fieldId, index }) => {
    if (fields?.length === 1) {
      return;
    }
    try {
      const updatedSections = getValues("sections");
      const clonedSections = structuredClone(updatedSections);
      dispatch(setFormData({ sections: clonedSections }));
     const resp= await dispatch(deleteField({ sectionId: selectedSection, fieldId })).unwrap()
     if(resp.success){
       notify.success(resp.message)
     }
      remove(index);

    } catch (err) {
      console.error(err);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAppendField = async () => {
    try {
          if (currentFormTemplate?.status === 2) {
      setSecOpenVersionIncreaseModal(true);   
      return;                  
    }
      setIsFieldAdd(true)

      await append({
        input_uuid: v4(),
        label: "Untitled field",
        order: fields.length + 1,
        placeholder: "Enter field title",
        property: {
          field_layout: 12,
          field_type: "Input Text",
        },
      });
      const updatedSections = getValues("sections");
      const clonedSections = structuredClone(updatedSections);
      dispatch(setFormData({ sections: clonedSections }))
      const response = await dispatch(sectionAutoSave({
        id: id,
        data: { sections: clonedSections }
      })).unwrap();
      if (response) {
        notify.success("Field added successfully")
      }
    } catch {
      setIsFieldAdd(false)
    }
    finally {
      setIsFieldAdd(false)
    }
  };
  const handleAcceptDelete = () => {
  if (sectionToDelete) {
    removeSection(sectionToDelete);
  }
  setSectionToDelete(null);
  setOpenDeleteModal(false);
};


  const hasFieldError = !!errors?.sections?.[index];
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

const handleFieldDragEnd = (event) => {
  const { active, over } = event;
  if (!over || active.id === over.id) return;

  const oldIndex = fields.findIndex((f) => f.input_uuid === active.id);
  const newIndex = fields.findIndex((f) => f.input_uuid === over.id);

  fieldMove(oldIndex, newIndex);

  const updatedSections = getValues("sections");
  updatedSections[index].fields = updatedSections[index].fields.map((field, idx) => ({
    ...field,
    order: idx + 1,
  }));
  dispatch(sectionAutoSave({
    id,
    data: { sections: updatedSections },
  }));
};
  const handleAccept = async () => {
    try {
      const response = await dispatch(incrementVersion(id)).unwrap();
      const newVersionId = response?.data?.newTemplate?.id;
      setSecOpenVersionIncreaseModal(false);
      navigate(`/bmr/form-management/${newVersionId}`);
    }
    catch (error) {
      console.error("Version increment failed:", error);
    }
  }
  const handleRejection = async () => {
    setSecOpenVersionIncreaseModal(false)
  }
  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`bg-white p-3 rounded-2xl relative mb-2`}
    >
      <Accordion
        key={section?.section_uuid}
        expanded={selectedSection === section?.section_uuid}
        onChange={(e, isExpanded) => { setSelectedSection(isExpanded ? section?.section_uuid : '');
          setSelectedField('');
        }}
        className={`shadow-none rounded-[10px] ${selectedSection === section?.section_uuid ? 'border-[0.1rem] border-blue-500' : 'border border-[#F0F1F5]'} ${hasFieldError && '!border-red-500'}`}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} className="ml-2">
          <Typography className="text-base text-[#828282] font-medium break-all capitalize">{index + 1}. {sectionName || 'Section name'} </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box onClick={() => setSelectedSection(section?.section_uuid)}>
            <FormField name={`sections.${index}.name`} label="Section name" placeholder="Enter section name" className="capitalize "/>
            <FormField name={`sections.${index}.description`} label="Section description" placeholder="Enter description"  className="capitalize "/>
           {/* @info make it none as not getting from BE */}
            {/* <FormField  sx={{ display: 'none' }} name={`sections.${index}.property.display_details`} type="switch" label="Section name and description will be displayed on the final form" /> */}
          </Box>
          <Grid container spacing={1}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleFieldDragEnd} autoScroll={true} >
              <SortableContext items={fields.map((f)=>f.input_uuid)} strategy={rectSortingStrategy}>
                {fields.map((field, ind) =>{
                  const layout= sectionSchema?.[index]?.fields?.[ind]?.property?.field_layout ?? "";               
                  if (active?.id === field.input_uuid) {
                    return (
                      <Grid key={`placeholder-${field.input_uuid}`} item xs={Number(layout) || 12} md={Number(layout) || 12}>
                        <Box
                          className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50"
                          sx={{ height: "5rem" }}
                        />
                      </Grid>
                    );
                  }
                return (
                  <Grid key={field.input_uuid} item xs={Number(layout) || 12} md={Number(layout) || 12}>
                    <FormInput
                    key={`property-${selectedSection}-${selectedField}`}
                      index={ind}
                      sectionIndex={index}
                      schema={field}
                      selectedField={selectedField}
                      setSelectedField={setSelectedField}
                      handleAppendField={handleAppendField}
                      handleRemoveField={handleRemoveField}
                      noOfFields={fields?.length}
                      isFieldAdd={isFieldAdd}
                    />
                  </Grid>)
              
                })}
                <DeleteModal
                  open={secOpenVersionIncreaseModal}
                  setClose={setSecOpenVersionIncreaseModal}
                  title={
                    <>
                      <span>You have already published this form.</span>
                      <br />
                      <span>If you want to add new fields, you will need to create a new version of the form.</span>
                      <br />
                      <span>Do you want to proceed?</span>
                    </>
                  }
                  buttonText="Yes"
                  fontWeight={300}
                  btnPrimary={"#90fb2cff"}
                  handleSuccess={handleAccept}
                  handleRejection={handleRejection}
                  rejectButtonText="No"
                  otherIcon={<ConfirmationInfoIcon Height={110} Width={110} Filled="#FB9D2C" />}
                />
              </SortableContext>
            </DndContext>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <div className="absolute bg-white shadow-md top-2 left-2 rounded-full cursor-grab active:cursor-grabbing">
        <DragIndicatorIcon className="m-1 text-[15px]" {...listeners} />
      </div>
      <div className="absolute flex -top-1 right-16 z-10">
        <span onClick={!isSectionAdd ? addNewSection : undefined} className={`${isSectionAdd? 'cursor-not-allowed' : 'cursor-pointer'}`} >
          <CloneIcon className="w-6 h-6 text-gray-600 hover:text-gray-800" />
        </span>
      {noOfSections !== 1 && ( <><div  onClick={handleOpen} className="cursor-pointer">
        <SettingIcon className="w-6 h-6 text-gray-600 hover:text-gray-800" />
        </div>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: {
            mt: 0.5,
            boxShadow: 3,
            borderRadius: 1.5,
            minWidth: 120,
          },
        }}
      >
            <MenuItem
              onClick={() => {
                setSectionToDelete({ sectionId: section?.section_uuid, index });
                setOpenDeleteModal(true);
                handleClose();
              }}
            >
              Remove
            </MenuItem>
          </Menu>
        </>
        )}
      </div>
        
      <DeleteModal
        open={openDeleteModal}
        setClose={() => {
          setOpenDeleteModal(false);
          setSectionToDelete(null);
        }}
        title="Are you sure you want to delete this section?"
        handleSuccess={handleAcceptDelete}
        handleRejection={() => {
          setOpenDeleteModal(false);
          setSectionToDelete(null);
        }}
        buttonText="Delete"
      />

    </Box>
  );
};


export default FormSection;
